// Gemensam tidslinje: knyter ihop skyddsronder, tillbud, arbetsberedningar,
// samordningsmöten, besiktningar och exporter i kronologisk ordning.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle,
  CalendarRange,
  FileDown,
  HardHat,
  History,
  Users,
  Wrench,
} from "lucide-react";

interface Props {
  projectId: string;
}

type EventKind =
  | "skyddsrond"
  | "tillbud"
  | "arbetsberedning"
  | "samordning"
  | "besiktning"
  | "export";

interface TimelineEvent {
  id: string;
  kind: EventKind;
  date: string;
  title: string;
  detail?: string;
  warning?: boolean;
}

const KIND_META: Record<
  EventKind,
  { label: string; icon: React.ReactNode }
> = {
  skyddsrond: { label: "Skyddsrond", icon: <HardHat className="h-4 w-4" /> },
  tillbud: { label: "Tillbud/olycka", icon: <AlertTriangle className="h-4 w-4" /> },
  arbetsberedning: { label: "Arbetsberedning", icon: <CalendarRange className="h-4 w-4" /> },
  samordning: { label: "Samordningsmöte", icon: <Users className="h-4 w-4" /> },
  besiktning: { label: "Besiktning", icon: <Wrench className="h-4 w-4" /> },
  export: { label: "Export", icon: <FileDown className="h-4 w-4" /> },
};

const FILTERS: { key: EventKind | "alla"; label: string }[] = [
  { key: "alla", label: "Allt" },
  { key: "skyddsrond", label: "Skyddsronder" },
  { key: "tillbud", label: "Tillbud" },
  { key: "arbetsberedning", label: "Arbetsberedningar" },
  { key: "samordning", label: "Samordning" },
  { key: "besiktning", label: "Besiktningar" },
  { key: "export", label: "Exporter" },
];

const fmt = (d: string) => new Date(d).toLocaleDateString("sv-SE");

const ProjectTimeline: React.FC<Props> = ({ projectId }) => {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [filter, setFilter] = useState<EventKind | "alla">("alla");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [rounds, incidents, preps, meetings, inspections, exports] = await Promise.all([
      supabase
        .from("safety_rounds")
        .select("id, name, round_date, area_name, status")
        .eq("project_id", projectId)
        .order("round_date", { ascending: false })
        .limit(50),
      supabase
        .from("incident_reports")
        .select("id, incident_type, occurred_at, location, status, requires_amp_revision")
        .eq("project_id", projectId)
        .order("occurred_at", { ascending: false })
        .limit(50),
      supabase
        .from("work_preparations")
        .select("id, name, created_at, status, review_status")
        .eq("project_id", projectId)
        .order("created_at", { ascending: false })
        .limit(50),
      supabase
        .from("coordination_meetings")
        .select("id, meeting_date, status")
        .eq("project_id", projectId)
        .order("meeting_date", { ascending: false })
        .limit(50),
      supabase
        .from("equipment_inspections")
        .select("id, object_name, last_inspected, next_inspection, result, status")
        .eq("project_id", projectId)
        .order("last_inspected", { ascending: false })
        .limit(50),
      supabase
        .from("document_exports")
        .select("id, document_name, doc_number:document_type, version, created_at")
        .eq("project_id", projectId)
        .order("created_at", { ascending: false })
        .limit(50),
    ]);

    const list: TimelineEvent[] = [];

    (rounds.data || []).forEach((r) => {
      if (!r.round_date) return;
      list.push({
        id: `sr-${r.id}`,
        kind: "skyddsrond",
        date: r.round_date,
        title: r.name || "Skyddsrond",
        detail: [r.area_name, r.status].filter(Boolean).join(" · "),
      });
    });

    (incidents.data || []).forEach((i) => {
      if (!i.occurred_at) return;
      list.push({
        id: `in-${i.id}`,
        kind: "tillbud",
        date: i.occurred_at,
        title: String(i.incident_type || "Tillbud").replace(/_/g, " "),
        detail: [i.location, i.status].filter(Boolean).join(" · "),
        warning: i.requires_amp_revision === true || i.status !== "closed",
      });
    });

    (preps.data || []).forEach((w) => {
      if (!w.created_at) return;
      list.push({
        id: `wp-${w.id}`,
        kind: "arbetsberedning",
        date: w.created_at,
        title: w.name || "Arbetsberedning",
        detail: [w.status, w.review_status].filter(Boolean).join(" · "),
        warning: w.review_status !== "godkand" && w.review_status !== "approved",
      });
    });

    (meetings.data || []).forEach((m) => {
      if (!m.meeting_date) return;
      list.push({
        id: `cm-${m.id}`,
        kind: "samordning",
        date: m.meeting_date,
        title: "Samordningsmöte",
        detail: m.status || undefined,
      });
    });

    (inspections.data || []).forEach((e) => {
      if (!e.last_inspected) return;
      list.push({
        id: `ei-${e.id}`,
        kind: "besiktning",
        date: e.last_inspected,
        title: e.object_name || "Besiktning",
        detail: [e.result, e.next_inspection ? `nästa ${fmt(e.next_inspection)}` : null]
          .filter(Boolean)
          .join(" · "),
        warning: e.result === "underkand" || e.status === "overdue",
      });
    });

    (exports.data || []).forEach((x) => {
      if (!x.created_at) return;
      list.push({
        id: `ex-${x.id}`,
        kind: "export",
        date: x.created_at,
        title: `${x.document_name || "Dokument"} v${x.version ?? 1}`,
        detail: "Word-export sparad i historiken",
      });
    });

    list.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    setEvents(list);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(
    () => (filter === "alla" ? events : events.filter((e) => e.kind === filter)),
    [events, filter]
  );

  const openWarnings = events.filter((e) => e.warning).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="h-5 w-5" />
          Tidslinje
          {openWarnings > 0 && (
            <Badge variant="destructive">{openWarnings} att följa upp</Badge>
          )}
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Allt som händer löpande i projektet i kronologisk ordning – bra underlag vid
          uppföljning, möten och tillsyn.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <Button
              key={f.key}
              size="sm"
              variant={filter === f.key ? "default" : "outline"}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </Button>
          ))}
        </div>

        {loading && <p className="text-sm text-muted-foreground">Laddar…</p>}

        {!loading && visible.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Inget registrerat ännu. Skyddsronder, tillbud och arbetsberedningar dyker upp
            här automatiskt.
          </p>
        )}

        <ol className="relative space-y-4 border-l pl-6">
          {visible.map((e) => (
            <li key={e.id} className="relative">
              <span className="absolute -left-[31px] flex h-6 w-6 items-center justify-center rounded-full border bg-background text-muted-foreground">
                {KIND_META[e.kind].icon}
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">{e.title}</span>
                <Badge variant="secondary">{KIND_META[e.kind].label}</Badge>
                {e.warning && <Badge variant="destructive">Följ upp</Badge>}
              </div>
              <p className="text-xs text-muted-foreground">
                {fmt(e.date)}
                {e.detail ? ` · ${e.detail}` : ""}
              </p>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
};

export default ProjectTimeline;
